import axios from 'axios';
import React, { useEffect, useState } from 'react';
import {
    MDBTabsPane,
    MDBInput,
    MDBDropdown,
    MDBDropdownToggle,
    MDBDropdownMenu,
    MDBDropdownItem,
} from "mdb-react-ui-kit";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import { exportToPDF, exportToCSV } from "../../../helper/exportDatatable";
import { usePdfUrls } from "../../../hooks/usePdfUrls";

const API_BASE_URL = process.env.REACT_APP_LAWS_BACKEND_HOST;
// Regex to detect any pattern like (1), (2), (3), etc.
const numberInParens = /\(\d+\)/;

const DocumentLink = ({ id, fileName, handleDocumentClick }) => (
    <div style={{ position: 'relative', zIndex: 9999 }}>
        <a
            href="#"
            style={{
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                maxWidth: '100%',
                WebkitTapHighlightColor: 'transparent',
                touchAction: 'manipulation'
            }}
            onClick={(e) => {
                console.log('Document click:', { id, fileName });
                handleDocumentClick(id, e);
            }}
            onTouchStart={(e) => {
                e.stopPropagation(); // Prevent touch event bubbling
            }}
        >
            {fileName}
        </a>
    </div>
);

const createParams = (bill, ordinal, legislatureOrdinal) => {
    const params = new URLSearchParams();
    params.append("legislatureOrdinal", `${legislatureOrdinal}`);
    params.append("sessionOrdinal", `${ordinal}`);
    params.append("billType", `${bill.billType.code}`);
    params.append("billNumber", `${bill.billNumber}`);
    return params;
};

const DocumentsAndLinks = ({ active, bill, ordinal, legislatureOrdinal }) => {
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        submittedDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
        fileNameRaw: { value: null, matchMode: FilterMatchMode.CONTAINS },
        type: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const { handleDocumentClick, fetchPdfUrl } = usePdfUrls();

    const fetchBillVersions = async (bill, ordinal, legislatureOrdinal) => {
        const params = createParams(bill, ordinal, legislatureOrdinal);
        const response = await axios.get(
            `${API_BASE_URL}/docs/v1/documents/getBillVersions?${params.toString()}`
        );
        // Filter out documents whose fileName contains (1), (2), etc.
        return response.data
            .filter(doc => !numberInParens.test(doc.fileName))
            .map(doc => ({
                ...doc,
                fileNameRaw: doc.fileName
            }));
    };

    const fetchBillOther = async (bill, ordinal, legislatureOrdinal) => {
        const params = createParams(bill, ordinal, legislatureOrdinal);
        try {
            const response = await axios.get(`${API_BASE_URL}/docs/v1/documents/getBillOther?${params.toString()}`);
            // Filter out documents whose fileName contains (1), (2), etc.
            return response.data
                .filter(doc => !numberInParens.test(doc.fileName))
                .map(doc => ({
                    ...doc,
                    fileNameRaw: doc.fileName
                }));
        } catch (error) {
            console.error('Error fetching other bill data: ', error);
            return [];
        }
    };

    const fetchAllBillData = async (bill, ordinal, legislatureOrdinal) => {
        const [billVersions, billOther] = await Promise.all([
            fetchBillVersions(bill, ordinal, legislatureOrdinal),
            fetchBillOther(bill, ordinal, legislatureOrdinal),
        ]);
        return [
            ...billVersions.map((doc) => ({ ...doc, type: "Bill Version" })),
            ...billOther.map((doc) => ({ ...doc, type: "Other" })),
        ];
    };

    useEffect(() => {
        const fetchAllDataAndUpdate = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const allDocuments = await fetchAllBillData(
                    bill,
                    ordinal,
                    legislatureOrdinal
                );
                setDocuments(allDocuments);
            } catch (err) {
                setError(err);
                console.error("Error in fetchAllDataAndUpdate:", err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllDataAndUpdate();
    }, [bill, ordinal, legislatureOrdinal]);

    useEffect(() => {
        if (isLoading && active) {
            // Function to update accordion height
            const updateAccordionHeight = () => {
                const accordionItem = document.querySelector(`[data-mdb-target="#collapse5"]`);
                if (accordionItem) {
                    const content = accordionItem.nextElementSibling;
                    if (content) {
                        content.style.height = 'auto';
                        // Force a reflow to ensure height is recalculated
                        content.offsetHeight;
                    }
                }
            };

            // Initial update
            setTimeout(updateAccordionHeight, 0);

            // Add resize listener
            window.addEventListener('resize', updateAccordionHeight);

            // Cleanup
            return () => window.removeEventListener('resize', updateAccordionHeight);
        }
    }, [isLoading, active]);

    if (!active) return null;

    const onGlobalFilterChange = (e) => {
        setFilters({
            global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS },
        });
    };

    const exportColumns = [
        { field: "type", header: "Type" },
        { field: "fileNameRaw", header: "File" },
    ];

    const handlePDFExport = () => {
        exportToPDF(
            "Documents",
            `${bill?.billType?.code}${bill?.billNumber}`,
            exportColumns,
            documents,
            {
                columnStyles: null,
            }
        );
    };

    const handleCSVExport = async () => {
        // Get URLs for all documents before exporting
        const dataWithUrls = await Promise.all(
            documents.map(async (doc) => {
                const pdfUrl = await fetchPdfUrl(doc.id);
                return {
                    Type: doc.type,
                    File: doc.fileNameRaw,
                    URL: pdfUrl || 'Not Available'
                };
            })
        );

        exportToCSV(
            "Documents",
            `${bill?.billType?.code}${bill?.billNumber}`,
            [
                { field: "Type", header: "Type" },
                { field: "File", header: "File" },
                { field: "URL", header: "URL" },
            ],
            dataWithUrls
        );
    };

    const emptyMessageTemplate = () => {
        return (
            <div style={{ textAlign: 'center', width: '100%' }}>
                No other documents are found.
            </div>
        );
    };

    return (
        <MDBTabsPane show={active}>
            <div className="row g-1">
                <div className="col-sm"></div>
                <div className="col-sm">
                    <MDBInput
                        className="mb-3"
                        label="Search Documents..."
                        type="text"
                        value={filters.global.value || ""}
                        onChange={onGlobalFilterChange}
                    />
                </div>
                <div className="col-auto">
                    <MDBDropdown className="d-flex justify-content-end mb-3">
                        <MDBDropdownToggle>
                            <i
                                className="fa-light fa-cloud-arrow-down"
                                aria-hidden="true"
                            ></i>{" "}
                            Export
                        </MDBDropdownToggle>
                        <MDBDropdownMenu>
                            <MDBDropdownItem
                                link
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleCSVExport();
                                }}
                            >
                                Excel/CSV
                            </MDBDropdownItem>
                            <MDBDropdownItem
                                link
                                onClick={(e) => {
                                    e.preventDefault();
                                    handlePDFExport();
                                }}
                            >
                                Print/PDF
                            </MDBDropdownItem>
                        </MDBDropdownMenu>
                    </MDBDropdown>
                </div>
            </div>
            {isLoading && <div>Loading...</div>}
            {!isLoading && (
                <DataTable
                    value={documents}
                    emptyMessage={"No documents available"}
                    paginator
                    rows={100}
                    rowsPerPageOptions={[25, 50, 100]}
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                    stripedRows
                    columnResizeMode="expand"
                    sortField="fileNameRaw"
                    sortOrder={-1}
                    removableSort
                    filters={filters}
                    globalFilter={globalFilter}
                    className="documents-table"
                    style={{ 
                        height: isLoading ? '200px' : 'auto',
                        minHeight: '200px',
                        position: 'relative',
                        zIndex: 1
                    }}
                    loading={isLoading}
                >
                    <Column
                        field="fileNameRaw"
                        header="File"
                        sortable
                        filter
                        body={(rowData) => (
                            <DocumentLink
                                id={rowData.id}
                                fileName={rowData.fileNameRaw}
                                handleDocumentClick={(id, e) => {
                                    console.log('Document click:', { id, fileName: rowData.fileNameRaw });
                                    handleDocumentClick(id, e);
                                }}
                            />
                        )}
                    />
                    <Column field="type" header="Type" sortable filter />
                </DataTable>
            )}
        </MDBTabsPane>
    );
};

export default DocumentsAndLinks;
